import { Progress } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { SelectedNodeContext } from '../../../contexts/node/SelectedNodeContext.ts';
import { IMission } from '../../../schema/Mission.ts';
import { computeMissionProgress, FormatDate } from '../../../utils/utils.ts';
import { MissionLifecycleBadge } from '../../ui/Badge.tsx';
import { LoadingButton } from '../../ui/Button.tsx';
import { EditMissionModal } from './MissionEditModal.tsx';

interface Props {
  mission: IMission;
  index: number;
}

export function MissionCard(props: Readonly<Props>) {
  const { t } = useTranslation();
  const [mission, setMission] = useState(props.mission);
  const [showEditMission, setShowEditMission] = useState(false);
  const { setSelectedNode } = useContext(SelectedNodeContext);
  const handleShowEditMission = () => setShowEditMission(true);

  useEffect(() => {
    if (props.mission != undefined) {
      setMission(props.mission);
    }
  }, [props.mission]);

  const onEnter = () => {
    setSelectedNode({ name: mission.dispatch_destination_node_name });
  };
  const onExit = () => {
    setSelectedNode({ name: null });
  };

  return (
    <Accordion.Item
      eventKey={String(props.index)}
      className={'basic-shadow ms-3 me-3 rounded-3 mb-2 mt-2'}>
      <Accordion.Header className={'rounded p-3'}>
        <div className={'w-100 d-flex flex-column'}>
          <div className={'row'}>
            <div className={'col '}>
              <p className={'text-bold mission-color'}>
                <img src={'/theme/media/target.svg'} width="20" height="20" alt="Target icon" />
                <span className={'ms-1'}>{mission.id}</span>
              </p>
            </div>
            <div className={'col'}>
              <p className={'text-basic'}>
                <img src={'/theme/media/small-av.svg'} width="20" height="20" alt="Small av icon" />
                <span className={'ms-1'}>{mission.vehicle_display_identifier}</span>
              </p>
            </div>
          </div>
          <div className={'row mt-2 mb-2'}>
            <div className={'col'}>
              <p className={'text-basic'}>
                <img
                  src={'/theme/media/note-pencil.svg'}
                  width="20"
                  height="20"
                  alt="Note pencil icon"
                />
                <span className={'ms-1'}>{mission.created_by}</span>
              </p>
            </div>
            <div className={'col'}>
              <MissionLifecycleBadge lifecycle={mission.lifecycle} />
            </div>
          </div>
          <div className={'row'}>
            <div className={'col'}>
              <p className={'text-basic geography-color'}>
                <img src={'/theme/media/pin.svg'} width="20" height="20" alt="Destination icon" />
                <span className={'ms-1'}>
                  {mission?.dispatch_destination_node_name?.toLowerCase() || '-'}
                </span>
              </p>
            </div>
            <div className={'col d-flex align-items-center'}>
              <Progress
                percent={Number(computeMissionProgress(mission))}
                size="small"
                strokeColor={'#4385e8'}
                format={percent => <span style={{ color: '#4385e8', fontWeight: 400 }}>{percent}%</span>}
              />
            </div>
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body className={'mb-1 text-center p-2 pt-0 mt-0'} onEnter={onEnter} onExit={onExit}>
        <div className={'container-fluid'}>
          {mission.shift_id && (
            <div className={'row mb-1'}>
              <div className={'col text-start'}>
                <p className={'text-bold shift-color'}>
                  <img src={'/theme/media/shift.svg'} width="20" height="20" alt="Shift icon" />
                  <span className={'ms-1'}>{mission.shift_id}</span>
                </p>
              </div>
            </div>
          )}
          <div className={'row w-100 m-0 p-0'}>
            <div className={'col-5 text-start m-0 p-0'}>
              <p className={'text-bold'}>{t('mission.created-at')}</p>
            </div>
            <div className={'col-7 text-end m-0 p-0'}>
              <p className={'text-basic'}>{FormatDate(mission.created_at)}</p>
            </div>
            <div className={'col-5 text-start m-0 p-0'}>
              <p className={'text-bold'}>{t('mission.updated-at')}</p>
            </div>
            <div className={'col-7 text-end m-0 p-0'}>
              <p className={'text-basic'}>{FormatDate(mission.updated_at)}</p>
            </div>
          </div>
          <div className={'row mt-3 mb-2'}>
            <div className={'col'}>
              <LoadingButton
                type={'submit-button'}
                text={t('mission.edit')}
                imagePath={'/theme/media/pencil.svg'}
                loading={false}
                onClick={() => handleShowEditMission()}
              />
            </div>
          </div>
          <EditMissionModal
            show={showEditMission}
            onHide={() => setShowEditMission(false)}
            mission={mission}
            onUpdated={newLifecycle => setMission({ ...mission, lifecycle: newLifecycle })}
          />
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
